import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@breakingthelines/design-system";

import { useArchetypePreview, type ArchetypePreviewState } from "@/hooks/use-archetype-preview";
import {
  useArchetype,
  useArchetypes,
  usePlayerMetricTemplate,
  usePublishArchetype,
  useRetireArchetype,
  useSaveArchetypeDraft,
} from "@/hooks/use-archetypes";
import {
  ARCHETYPE_HOME_POSITIONS,
  getArchetype,
  type ArchetypeDefinition,
  type ArchetypeDetail,
  type ArchetypeVersion,
  type MetricDefinition,
} from "@/lib/admin-api";
import { HttpError } from "@/lib/http-client";

import {
  DEFAULT_WEIGHT,
  EMPTY_FORM,
  NAME_MAX,
  NOTES_MAX,
  WEIGHT_MAX,
  WEIGHT_MIN,
  WEIGHT_STEP,
  definitionFromForm,
  formFromVersion,
  pickableMetrics,
  positionLabel,
  sameDefinition,
  seedFromDetail,
  slugFromName,
  validArchetypeId,
  validateForm,
  weightDataClass,
  weightLabel,
  type ArchetypeFormState,
  type EditorSeed,
} from "./archetype-form";
import { ArchetypeHistory } from "./archetype-history";
import { ArchetypePreview } from "./archetype-preview";
import {
  canPreview,
  flaggedLine,
  flaggedOverlaps,
  overlapCheckComplete,
} from "./archetype-preview-model";
import styles from "./archetype-editor.module.css";

const ID_TAKEN = "An archetype with this id exists.";

function isStatus(error: unknown, status: number): boolean {
  return error instanceof HttpError && error.status === status;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** True when the stored version holds exactly the definition that was sent. */
function holdsDefinition(version: ArchetypeVersion, sent: ArchetypeDefinition): boolean {
  return JSON.stringify(definitionFromForm(formFromVersion(version))) === JSON.stringify(sent);
}

/**
 * The archetype editor (profile-spec.md §5), keyed by id so moving from one
 * archetype to another never carries a form or a revision across.
 */
export function ArchetypeEditorPage() {
  const { archetypeId } = useParams();
  return <ArchetypeEditor key={archetypeId ?? "new"} archetypeId={archetypeId} />;
}

/**
 * # CONCURRENCY
 *
 * The draft's revision is the only concurrency control, as in the platform
 * page editor: no live collaboration. The editor keeps the revision it last
 * read in its own state, never from a refetch, so a save always says which
 * draft it was made against. A stale save or publish comes back 409 and the
 * editor offers a reload, which reseeds the form from the latest versions.
 *
 * # RETRIES
 *
 * Prod's /v1/admin/ route retries a request on a reset, so a write can land
 * and its retry fail: a save as 409, a publish or retire as 404. On those
 * answers the editor re-reads the archetype and counts the write as done only
 * when the stored state is exactly what this editor asked for (the same
 * definition, the same draft revision, the same version).
 *
 * # NEW ARCHETYPES
 *
 * The BFF has no create-only save: revision 0 on an existing id writes a new
 * draft of that archetype. So /new saves only once the library list has
 * loaded, checks the id is unknown (404) right before the first save, and
 * reads a 409 as the id being taken, never as a stale draft.
 *
 * # PREVIEW (PF-A2b)
 *
 * `definition` below is exactly what a save sends, less the revision, and the
 * preview scores that same value as it is typed. The publish confirm lists
 * the preview's flagged overlaps: publishing is allowed only with no unsaved
 * edits, so what the preview scored is the draft being published.
 *
 * # THE FIRST SAVE ON /new
 *
 * The first save moves /new to /archetypes/{id}, a different route, so this
 * component remounts there. The "Draft saved" notice travels in the
 * navigation state and is read once on the new mount.
 */
function ArchetypeEditor({ archetypeId }: { archetypeId?: string }) {
  const isNew = !archetypeId;

  const detailQuery = useArchetype(archetypeId);
  const library = useArchetypes({ enabled: isNew });
  const { template, loading: templateLoading } = usePlayerMetricTemplate();
  const saveMutation = useSaveArchetypeDraft();
  const publishMutation = usePublishArchetype();
  const retireMutation = useRetireArchetype();
  const navigate = useNavigate();
  const location = useLocation();
  const carriedNotice = (location.state as { notice?: unknown } | null)?.notice;

  const [seed, setSeed] = useState<EditorSeed | null>(
    isNew ? { form: EMPTY_FORM, expectedRevision: 0, draftVersion: 0 } : null,
  );
  const [form, setForm] = useState<ArchetypeFormState>(EMPTY_FORM);
  const [conflict, setConflict] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    typeof carriedNotice === "string" ? carriedNotice : null,
  );
  const [confirm, setConfirm] = useState<"publish" | "retire" | null>(null);
  /** Ids game-service says exist that the library list did not show. */
  const [takenIds, setTakenIds] = useState<ReadonlySet<string>>(new Set());
  const [checkingId, setCheckingId] = useState(false);

  const applySeed = useCallback((next: EditorSeed) => {
    setSeed(next);
    setForm(next.form);
    setSubmitted(false);
  }, []);

  // The notice carried from /new is shown once: the state is cleared so a
  // reload of this page does not repeat it.
  useEffect(() => {
    if (carriedNotice !== undefined) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null },
      );
    }
  }, [carriedNotice, navigate, location.pathname, location.search]);

  // Seed once, from the first answer. Later refetches (after a write) update
  // the history and status line but never the form or the revision.
  useEffect(() => {
    if (seed === null && detailQuery.data) applySeed(seedFromDetail(detailQuery.data));
  }, [seed, detailQuery.data, applySeed]);

  const metrics = useMemo(
    () => new Map<string, MetricDefinition>((template?.metrics ?? []).map((m) => [m.key, m])),
    [template],
  );
  const definition = useMemo(() => definitionFromForm(form), [form]);
  const problems = useMemo(() => validateForm(form, metrics), [form, metrics]);
  const previewable = useMemo(() => canPreview(form, metrics), [form, metrics]);
  const preview = useArchetypePreview({
    archetypeId: archetypeId ?? "",
    definition,
    enabled: previewable,
  });
  const dirty = seed !== null && !sameDefinition(form, seed.form);
  const takenIdList = useMemo(() => {
    const ids = new Set(takenIds);
    for (const a of library.archetypes) ids.add(a.archetypeId);
    return ids;
  }, [takenIds, library.archetypes]);

  const busy =
    checkingId || saveMutation.isPending || publishMutation.isPending || retireMutation.isPending;

  const markTaken = useCallback((id: string) => {
    setTakenIds((prev) => new Set(prev).add(id));
  }, []);

  const handleReload = useCallback(async () => {
    setActionError(null);
    setNotice(null);
    const result = await detailQuery.refetch();
    if (result.data) {
      applySeed(seedFromDetail(result.data));
      setConflict(false);
    } else {
      setActionError(errorMessage(result.error, "Failed to reload archetype."));
    }
  }, [detailQuery, applySeed]);

  /** The archetype as stored now, or undefined when it cannot be read. */
  const reread = useCallback(
    async (id: string): Promise<ArchetypeDetail | undefined> => {
      if (!isNew) return (await detailQuery.refetch()).data;
      try {
        return await getArchetype(id);
      } catch {
        return undefined;
      }
    },
    [isNew, detailQuery],
  );

  const settleSaved = (draft: ArchetypeVersion) => {
    applySeed({
      form: formFromVersion(draft),
      expectedRevision: draft.revision,
      draftVersion: draft.version,
    });
    setNotice("Draft saved");
    if (isNew) {
      navigate(`/archetypes/${draft.archetypeId}`, {
        replace: true,
        state: { notice: "Draft saved" },
      });
    }
  };

  const save = async (newId: string) => {
    setSubmitted(true);
    setActionError(null);
    setNotice(null);
    if (!seed || problems.length > 0) return;
    const id = isNew ? newId : archetypeId;
    if (!id) return;
    const sent = definition;
    const expectedRevision = seed.expectedRevision;

    if (isNew) {
      setCheckingId(true);
      try {
        await getArchetype(id);
        markTaken(id);
        return;
      } catch (error) {
        if (!isStatus(error, 404)) {
          setActionError(errorMessage(error, "Failed to save draft."));
          return;
        }
      } finally {
        setCheckingId(false);
      }
    }

    saveMutation.mutate(
      { archetypeId: id, expectedRevision, ...sent },
      {
        onSuccess: ({ draft }) => settleSaved(draft),
        onError: async (error) => {
          if (!isStatus(error, 409)) {
            setActionError(errorMessage(error, "Failed to save draft."));
            return;
          }
          const draft = (await reread(id))?.draft;
          if (draft && draft.revision !== expectedRevision && holdsDefinition(draft, sent)) {
            settleSaved(draft);
            return;
          }
          if (isNew) markTaken(id);
          else setConflict(true);
        },
      },
    );
  };

  const publish = () => {
    if (!seed || !archetypeId) return;
    const version = seed.draftVersion;
    const expectedRevision = seed.expectedRevision;
    const settlePublished = (published: ArchetypeVersion) => {
      applySeed({ form: formFromVersion(published), expectedRevision: 0, draftVersion: 0 });
      setConfirm(null);
      setNotice(`v${published.version} published`);
    };
    setActionError(null);
    publishMutation.mutate(
      { archetypeId, version, expectedRevision },
      {
        onSuccess: ({ published }) => settlePublished(published),
        onError: async (error) => {
          if (isStatus(error, 404)) {
            // Publishing keeps the draft's revision, so a landed first try
            // shows this version published at the revision this editor read.
            // Anything else is someone else's version.
            const latest = await reread(archetypeId);
            const published = latest?.published;
            if (published?.version === version && published.revision === expectedRevision) {
              settlePublished(published);
              return;
            }
            if (latest) {
              setConfirm(null);
              setConflict(true);
              return;
            }
          }
          setConfirm(null);
          if (isStatus(error, 409)) setConflict(true);
          else setActionError(errorMessage(error, "Failed to publish."));
        },
      },
    );
  };

  const openRetire = async () => {
    setConfirm("retire");
    // The dialog names the published version, so it is read fresh: the
    // cached one can be up to 30 s old.
    const result = await detailQuery.refetch();
    if (result.data && !result.data.published) setConfirm(null);
  };

  const retire = () => {
    if (!archetypeId) return;
    const named = detailQuery.data?.published?.version;
    setActionError(null);
    retireMutation.mutate(archetypeId, {
      onSuccess: ({ retired }) => {
        setConfirm(null);
        setNotice(`v${retired.version} retired`);
      },
      onError: async (error) => {
        if (isStatus(error, 404) && named) {
          // A retried retire that landed finds nothing published.
          const latest = await reread(archetypeId);
          if (latest && !latest.published) {
            setConfirm(null);
            setNotice(`v${named} retired`);
            return;
          }
          if (latest && latest.published?.version !== named) {
            setConfirm(null);
            setConflict(true);
            return;
          }
        }
        setConfirm(null);
        setActionError(errorMessage(error, "Failed to retire."));
      },
    });
  };

  if (!isNew) {
    if (detailQuery.isPending) return <p className={styles.notice}>Loading archetype…</p>;
    if (detailQuery.isError || !detailQuery.data) {
      return (
        <div className={styles.page}>
          <Link className={styles.back} to="/archetypes">
            Archetypes
          </Link>
          <p className={styles.error}>
            {isStatus(detailQuery.error, 404)
              ? "No archetype with this id."
              : (detailQuery.error?.message ?? "Failed to load archetype.")}
          </p>
        </div>
      );
    }
  }

  const detail: ArchetypeDetail | undefined = detailQuery.data;

  return (
    <ArchetypeEditorView
      isNew={isNew}
      archetypeId={archetypeId}
      detail={detail}
      title={seed?.form.name || detail?.versions[0]?.name || "New archetype"}
      form={form}
      onFormChange={(next) => {
        setForm(next);
        setNotice(null);
      }}
      metrics={metrics}
      templateMetrics={template?.metrics ?? []}
      templateLoading={templateLoading}
      problems={submitted ? problems : []}
      dirty={dirty}
      busy={busy}
      conflict={conflict}
      reloading={detailQuery.isFetching}
      onReload={() => void handleReload()}
      actionError={actionError}
      notice={notice}
      takenIds={takenIdList}
      libraryReady={!isNew || (!library.loading && !library.failed)}
      libraryFailed={isNew && library.failed}
      canPublish={Boolean(seed && seed.draftVersion > 0) && !dirty}
      canRetire={Boolean(detail?.published)}
      onSave={(newId) => void save(newId)}
      confirm={confirm}
      onConfirmChange={(next) => {
        if (next === "retire") void openRetire();
        else setConfirm(next);
      }}
      onPublish={publish}
      onRetire={retire}
      preview={preview}
    />
  );
}

interface ArchetypeEditorViewProps {
  isNew: boolean;
  archetypeId?: string;
  detail?: ArchetypeDetail;
  /** The name as last loaded or saved, not as typed. */
  title: string;
  form: ArchetypeFormState;
  onFormChange: (form: ArchetypeFormState) => void;
  metrics: ReadonlyMap<string, MetricDefinition>;
  templateMetrics: readonly MetricDefinition[];
  templateLoading: boolean;
  problems: string[];
  dirty: boolean;
  busy: boolean;
  conflict: boolean;
  reloading: boolean;
  onReload: () => void;
  actionError: string | null;
  notice: string | null;
  /** Ids that exist, for /new. */
  takenIds: ReadonlySet<string>;
  /** /new saves only once the library list has loaded. */
  libraryReady: boolean;
  libraryFailed: boolean;
  canPublish: boolean;
  canRetire: boolean;
  onSave: (newId: string) => void;
  confirm: "publish" | "retire" | null;
  onConfirmChange: (confirm: "publish" | "retire" | null) => void;
  onPublish: () => void;
  onRetire: () => void;
  preview: ArchetypePreviewState;
}

/** "Published v3. Draft v4." Plain text, no chips. */
export function versionLine(detail: ArchetypeDetail | undefined): string {
  if (!detail) return "";
  const parts: string[] = [];
  if (detail.published) parts.push(`Published v${detail.published.version}`);
  if (detail.draft) parts.push(`Draft v${detail.draft.version}`);
  if (parts.length === 0 && detail.versions[0]) {
    parts.push(`Retired v${detail.versions[0].version}`);
  }
  return parts.length > 0 ? `${parts.join(". ")}.` : "";
}

function ArchetypeEditorView({
  isNew,
  archetypeId,
  detail,
  title,
  form,
  onFormChange,
  metrics,
  templateMetrics,
  templateLoading,
  problems,
  dirty,
  busy,
  conflict,
  reloading,
  onReload,
  actionError,
  notice,
  takenIds,
  libraryReady,
  libraryFailed,
  canPublish,
  canRetire,
  onSave,
  confirm,
  onConfirmChange,
  onPublish,
  onRetire,
  preview,
}: ArchetypeEditorViewProps) {
  const flags = flaggedOverlaps(preview.groups);
  const overlapChecked = overlapCheckComplete(preview.groups, preview.current);
  const [newId, setNewId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const effectiveId = idTouched ? newId : slugFromName(form.name);
  const idTaken = isNew && takenIds.has(effectiveId);
  const idProblem = !isNew
    ? null
    : !validArchetypeId(effectiveId)
      ? "Id must be lowercase letters, numbers and hyphens."
      : idTaken
        ? ID_TAKEN
        : null;
  const showIdProblem = Boolean(idProblem && (idTouched || form.name.trim()));
  const fieldIds = useId();
  const idInputId = `${fieldIds}-id`;
  const idErrorId = `${fieldIds}-id-error`;
  const problemsId = `${fieldIds}-problems`;
  const describedByProblems = problems.length > 0 ? problemsId : undefined;
  const nameInvalid = problems.some((p) => p.startsWith("Name"));

  const pickable = useMemo(
    () => pickableMetrics(templateMetrics, form.weights),
    [templateMetrics, form.weights],
  );
  const families = useMemo(() => {
    const byFamily = new Map<string, MetricDefinition[]>();
    for (const metric of pickable) {
      const family = metric.family || "Other";
      byFamily.set(family, [...(byFamily.get(family) ?? []), metric]);
    }
    return [...byFamily.entries()];
  }, [pickable]);

  const update = (patch: Partial<ArchetypeFormState>) => onFormChange({ ...form, ...patch });

  const published = detail?.published;
  const draftVersion = detail?.draft?.version;
  const draftName = detail?.draft?.name || title;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link className={styles.back} to="/archetypes">
          Archetypes
        </Link>
        <h1 className={styles.title}>{isNew ? "New archetype" : title}</h1>
        {!isNew ? (
          <p className={styles.versionLine}>
            <span className={styles.id}>{archetypeId}</span> {versionLine(detail)}
          </p>
        ) : null}
      </div>

      {conflict ? (
        <div className={styles.conflict} role="alert">
          <p className={styles.conflictText}>
            This archetype changed since you opened it. Reloading discards your edits.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={reloading}
            onClick={onReload}
          >
            {reloading ? "Reloading…" : "Reload"}
          </Button>
        </div>
      ) : null}

      <div className={styles.workspace}>
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (idProblem) return;
            onSave(effectiveId);
          }}
        >
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <Input
              value={form.name}
              maxLength={NAME_MAX}
              aria-invalid={nameInvalid || undefined}
              aria-describedby={describedByProblems}
              onChange={(event) => update({ name: event.currentTarget.value })}
            />
          </label>

          {isNew ? (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={idInputId}>
                Id
              </label>
              <Input
                id={idInputId}
                value={effectiveId}
                maxLength={64}
                aria-invalid={showIdProblem || undefined}
                aria-describedby={showIdProblem ? idErrorId : undefined}
                onChange={(event) => {
                  setIdTouched(true);
                  setNewId(event.currentTarget.value);
                }}
              />
              {showIdProblem ? (
                <span id={idErrorId} className={styles.fieldError} role="alert">
                  {idProblem}
                </span>
              ) : null}
            </div>
          ) : null}

          <fieldset className={styles.fieldset} aria-describedby={describedByProblems}>
            <legend className={styles.label}>Home positions</legend>
            <div className={styles.checkGrid}>
              {ARCHETYPE_HOME_POSITIONS.map((position) => (
                <label key={position} className={styles.check}>
                  <input
                    type="checkbox"
                    checked={form.homePositions.includes(position)}
                    onChange={(event) =>
                      update({
                        homePositions: event.currentTarget.checked
                          ? [...form.homePositions, position]
                          : form.homePositions.filter((p) => p !== position),
                      })
                    }
                  />
                  {positionLabel(position)}
                </label>
              ))}
            </div>
          </fieldset>

          <div className={styles.checkRow}>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={form.positional}
                onChange={(event) => update({ positional: event.currentTarget.checked })}
              />
              Positional
            </label>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={form.eventOnly}
                onChange={(event) => update({ eventOnly: event.currentTarget.checked })}
              />
              Event data only
            </label>
          </div>

          <fieldset className={styles.fieldset} aria-describedby={describedByProblems}>
            <legend className={styles.label}>Metric weights</legend>
            {form.weights.length > 0 ? (
              <ul className={styles.weights}>
                {form.weights.map((row, index) => {
                  const label = weightLabel(row, metrics);
                  return (
                    <li key={row.metricKey} className={styles.weightRow}>
                      <span className={styles.metricName}>
                        {label}
                        {weightDataClass(row, metrics) === "event" ? (
                          <span className={styles.muted}> Event data</span>
                        ) : null}
                      </span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={WEIGHT_MIN}
                        max={WEIGHT_MAX}
                        step={WEIGHT_STEP}
                        aria-label={`${label} weight`}
                        className={styles.weightInput}
                        value={row.weight}
                        onChange={(event) => {
                          const weights = [...form.weights];
                          weights[index] = { ...row, weight: event.currentTarget.value };
                          update({ weights });
                        }}
                      />
                      <button
                        type="button"
                        className={styles.remove}
                        aria-label={`Remove ${label}`}
                        onClick={() =>
                          update({
                            weights: form.weights.filter((w) => w.metricKey !== row.metricKey),
                          })
                        }
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            <div className={styles.addMetric}>
              <Select
                value=""
                disabled={templateLoading || pickable.length === 0}
                onValueChange={(value: string | null) => {
                  if (!value) return;
                  const metric = metrics.get(value);
                  update({
                    weights: [
                      ...form.weights,
                      {
                        metricKey: value,
                        weight: String(DEFAULT_WEIGHT),
                        storedLabel: metric?.label ?? value,
                        storedDataClass: metric?.dataClass ?? "",
                      },
                    ],
                  });
                }}
              >
                <SelectTrigger aria-label="Add metric" className={styles.addTrigger}>
                  <SelectValue>{() => "Add metric"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {families.map(([family, familyMetrics]) => (
                    <SelectGroup key={family}>
                      <SelectLabel>{family}</SelectLabel>
                      {familyMetrics.map((metric) => (
                        <SelectItem key={metric.key} value={metric.key}>
                          {metric.dataClass === "event"
                            ? `${metric.label}, event data`
                            : metric.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {!templateLoading && templateMetrics.length === 0 ? (
                <span className={styles.muted}>Metric list unavailable</span>
              ) : null}
            </div>
          </fieldset>

          <label className={styles.field}>
            <span className={styles.label}>Notes</span>
            <textarea
              className={styles.textarea}
              rows={3}
              maxLength={NOTES_MAX}
              value={form.notes}
              onChange={(event) => update({ notes: event.currentTarget.value })}
            />
          </label>

          {problems.length > 0 ? (
            <div id={problemsId} role="alert">
              <ul className={styles.problems}>
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className={styles.footer}>
            <Button
              type="submit"
              disabled={busy || conflict || !libraryReady || Boolean(idProblem)}
            >
              Save draft
            </Button>
            {!isNew ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy || conflict || !canPublish}
                  onClick={() => onConfirmChange("publish")}
                >
                  Publish
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || conflict || !canRetire}
                  onClick={() => onConfirmChange("retire")}
                >
                  Retire
                </Button>
              </>
            ) : null}
            {libraryFailed ? (
              <span className={styles.muted}>Archetype list unavailable</span>
            ) : null}
            {dirty ? <span className={styles.muted}>Unsaved changes</span> : null}
            <span className={styles.saved} role="status">
              {!dirty && notice ? notice : null}
            </span>
          </div>
          {actionError ? (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          ) : null}
        </form>

        <ArchetypePreview state={preview} hasHomePositions={form.homePositions.length > 0} />
      </div>

      {detail ? <ArchetypeHistory versions={detail.versions} /> : null}

      <AlertDialog
        open={confirm != null}
        onOpenChange={(open) => {
          if (!open && !busy) onConfirmChange(null);
        }}
      >
        <AlertDialogContent>
          {confirm === "publish" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Publish {draftName} v{draftVersion}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {published
                    ? `It replaces v${published.version} on player pages after the next nightly run.`
                    : "It shows on player pages after the next nightly run."}
                </AlertDialogDescription>
                {flags.length > 0 ? (
                  <div className={styles.flags}>
                    <p className={styles.flagsTitle}>
                      {flags.length === 1 ? "Overlap above 0.8" : "Overlaps above 0.8"}
                    </p>
                    <ul className={styles.flagList}>
                      {flags.map((flag) => (
                        <li key={`${flag.position}-${flag.archetypeId}`}>{flaggedLine(flag)}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {overlapChecked ? null : (
                  <p className={styles.muted}>
                    {flags.length > 0 ? "Overlap check incomplete" : "Overlap check unavailable"}
                  </p>
                )}
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction disabled={busy} onClick={onPublish}>
                  {busy ? "Publishing…" : "Publish"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
          {confirm === "retire" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Retire {published?.name || title} v{published?.version}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  It leaves player pages after the next nightly run.
                  {draftVersion ? ` The draft v${draftVersion} is kept.` : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  disabled={busy || reloading || !published}
                  onClick={onRetire}
                >
                  {busy ? "Retiring…" : "Retire"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
